import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, Download, ShieldCheck, Moon, Sun, Star } from "lucide-react";
import ProfileDialog from "./ProfileDialog";
import { siteConfig } from "../lib/siteConfig";
import { useTheme } from "../context/ThemeContext";
import { VerifiedName } from "./VerifiedBadge";

export default function Navbar({ theme = "light" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user && user !== false && user.role === "admin";
  const isAuthed = user && user !== false && !isAdmin;
  const [profileOpen, setProfileOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const isLight = !isDark && theme === "light";
  const shellClass = isLight
    ? "safe-area-navbar sticky top-0 z-50 border-b border-slate-200 bg-white/90 text-slate-950 backdrop-blur-2xl"
    : "safe-area-navbar sticky top-0 z-50 bg-[#05070D]/70 backdrop-blur-2xl border-b border-white/5";
  const inactiveNavClass = isLight ? "text-slate-600 hover:text-slate-950" : "text-white/70 hover:text-white";
  const softButtonClass = isLight
    ? "border border-slate-200 bg-slate-100 text-slate-800 hover:bg-slate-200"
    : "border border-white/10 bg-white/[0.04] hover:bg-white/[0.08]";
  const iconButtonClass = isLight
    ? "p-2.5 rounded-full hover:bg-slate-100 border border-transparent hover:border-slate-200"
    : "p-2.5 rounded-full hover:bg-white/5 border border-transparent hover:border-white/10";
  const displayName = user && user !== false ? (user.username || user.email || "Player") : "Player";
  const profileInitial = displayName.charAt(0).toUpperCase();

  useEffect(() => {
    if (!profileOpen) return undefined;
    document.body.classList.add("profile-dialog-open");
    return () => document.body.classList.remove("profile-dialog-open");
  }, [profileOpen]);

  return (
    <header className={shellClass}>
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-2 px-3 sm:h-20 sm:px-6 md:px-10">
        <Link to="/" data-testid="nav-logo" className="flex min-w-0 items-center gap-2 group">
          <img src={siteConfig.logoUrl} alt={siteConfig.name} className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-[#2F6FA5]/60 sm:h-10 sm:w-10" />
          <span className="hidden whitespace-nowrap font-display text-lg tracking-tight lg:inline lg:text-xl">{siteConfig.name}</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 text-sm">
          {[
            ["Games", "/games"],
            ["Leaderboard", "/leaderboard"],
            ["Reviews", "/reviews"],
          ].map(([label, to]) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-link-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `transition-colors ${isActive ? "text-[#2F6FA5]" : inactiveNavClass}`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-1 lg:gap-3">
          <Link to="/reviews" title="Player reviews" aria-label="Player reviews" className={`grid h-9 w-9 shrink-0 place-items-center rounded-full lg:hidden ${softButtonClass}`}><Star className="h-4 w-4" /></Link>
          <button
            type="button"
            onClick={toggleTheme}
            data-testid="theme-toggle"
            title={isDark ? "Switch to day mode" : "Switch to night mode"}
            className={`inline-flex h-9 w-9 items-center justify-center gap-1.5 rounded-full text-xs font-semibold transition-colors sm:h-auto sm:w-auto sm:px-2.5 sm:py-2 ${softButtonClass}`}
            aria-label={isDark ? "Switch to day mode" : "Switch to night mode"}
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            <span className="hidden min-[430px]:inline">{isDark ? "Day" : "Night"}</span>
          </button>

          {!isAdmin && (
            <a
              href={siteConfig.androidAppDownloadUrl}
              download="FEA-Slots.apk"
              data-testid="nav-download-app"
              title={`Download ${siteConfig.name} Android app`}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center gap-1.5 rounded-full border border-[#2F6FA5]/40 text-xs font-bold text-[#2F6FA5] transition-colors hover:bg-[#2F6FA5]/10 lg:h-auto lg:w-auto lg:px-3 lg:py-1.5"
            >
              <Download className="h-3.5 w-3.5" />
              <span className="hidden lg:inline">Download App</span>
            </a>
          )}

          {isAdmin ? (
            <>
              <Link
                to="/admin/dashboard"
                data-testid="nav-admin-console"
                className={`hidden sm:inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${isLight ? "border border-[#2F6FA5]/30 bg-[#2F6FA5]/10 text-slate-900 hover:bg-[#2F6FA5]/15" : "border border-[#2F6FA5]/35 bg-white/[0.04] text-white/85 hover:bg-white/[0.08] hover:text-white"}`}
              >
                <ShieldCheck className="h-4 w-4 text-[#2F6FA5]" /> Admin console
              </Link>
              <button onClick={() => { logout(); nav("/"); }} data-testid="nav-logout" title="Sign out"
                className={iconButtonClass}>
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : isAuthed ? (
            <>
              <button
                type="button"
                onClick={() => setProfileOpen(true)}
                data-testid="nav-profile-mobile"
                title="Open profile"
                className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full transition-colors lg:hidden ${softButtonClass}`}
                aria-label="Open profile"
              >
                {user.profile_image_url ? (
                  <img src={user.profile_image_url} alt="" className="h-full w-full rounded-full object-cover" />
                ) : (
                  <span className="grid h-full w-full place-items-center rounded-full bg-gradient-to-br from-[#2F6FA5] to-[#173A5D] font-display text-base font-black text-white">
                    {profileInitial}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setProfileOpen(true)}
                data-testid="nav-profile"
                className={`hidden lg:flex items-center gap-2.5 pl-2 pr-4 py-1.5 rounded-full transition-colors ${softButtonClass}`}
              >
                {user.profile_image_url ? (
                  <img src={user.profile_image_url} alt="" className="w-9 h-9 rounded-full object-cover ring-1 ring-[#2F6FA5]/50" />
                ) : (
                  <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#2F6FA5] to-[#173A5D] grid place-items-center font-display font-black text-white text-base">
                    {profileInitial}
                  </span>
                )}
                <VerifiedName className="font-semibold text-base" name={displayName} user={user} />
              </button>
              <button onClick={() => { logout(); nav("/"); }} data-testid="nav-logout" title="Sign out"
                className={iconButtonClass}>
                <LogOut className="w-4 h-4" />
              </button>
              <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className={`whitespace-nowrap px-1.5 py-2 text-xs sm:px-3 sm:text-sm ${isLight ? "text-slate-700 hover:text-slate-950" : "text-white/80 hover:text-white"}`}>Sign in</Link>
              <Link to="/signup" data-testid="nav-signup" className="whitespace-nowrap rounded-full bg-[#2F6FA5] px-3 py-2 text-xs font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#4E91C8] sm:px-5 sm:text-sm">
                <span className="sm:hidden">Join</span>
                <span className="hidden sm:inline">Create account</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
